import type { QuickBooksFpaStatementRow } from '../../../data/ServerApiClient'

type SectionKey = 'income' | 'costOfGoodsSold' | 'expenses' | 'otherIncome' | 'otherExpenses'

const sections: Record<SectionKey, { headingId: string; totalId: string; labels: string[] }> = {
  income: { headingId: 'fpa:pnl:revenue', totalId: 'fpa:pnl:total_revenue', labels: ['income', 'revenue'] },
  costOfGoodsSold: { headingId: 'fpa:pnl:cost_of_sales', totalId: 'fpa:pnl:total_cost_of_sales', labels: ['cost of goods sold', 'cost of sales'] },
  expenses: { headingId: 'fpa:pnl:expenses', totalId: 'fpa:pnl:total_expenses', labels: ['expenses', 'expense'] },
  otherIncome: { headingId: 'fpa:pnl:other_income', totalId: 'fpa:pnl:total_other_income', labels: ['other income'] },
  otherExpenses: { headingId: 'fpa:pnl:other_expense', totalId: 'fpa:pnl:total_other_expenses', labels: ['other expenses', 'other expense'] },
}

const labelOf = (row: QuickBooksFpaStatementRow) => row.label.trim().toLowerCase().replace(/\s+/g, ' ')
const levelOf = (row: QuickBooksFpaStatementRow) => Number(row.level || 0)
const outermost = (rows: QuickBooksFpaStatementRow[]) => rows.reduce<QuickBooksFpaStatementRow | null>(
  (selected, row) => !selected || levelOf(row) < levelOf(selected) ? row : selected,
  null,
)

/** Canonical sections can contain layout subtotals with the same display label.
 * Keep the outer total and all its details together instead of taking the first
 * label match. Plain provider statements use their outer heading/total levels.
 */
export function selectProfitAndLossSection(rows: QuickBooksFpaStatementRow[], key: SectionKey) {
  const definition = sections[key]
  const heading = rows.find((row) => row.node_id === definition.headingId)
    || outermost(rows.filter((row) => definition.labels.includes(labelOf(row)) && !row.is_formula
      && row.row_type !== 'account' && row.row_type !== 'line' && !row.account_id))
  const start = heading ? rows.indexOf(heading) : -1
  const canonicalTotal = rows.find((row) => row.node_id === definition.totalId) || null
  let end = canonicalTotal ? rows.indexOf(canonicalTotal) : rows.length
  if (!canonicalTotal && heading) {
    for (let index = start + 1; index < rows.length; index += 1) {
      const row = rows[index]
      if (levelOf(row) > levelOf(heading) || row.row_type === 'account' || row.row_type === 'line') continue
      end = index
      break
    }
  }
  const totalLabels = definition.labels.map((label) => `total ${label}`)
  const total = canonicalTotal || outermost(rows.filter((row, index) =>
    index > start && index <= end && totalLabels.includes(labelOf(row))
      && (!heading || levelOf(row) <= levelOf(heading)),
  ))
  if (total) end = rows.indexOf(total)
  const details = start >= 0 ? rows.slice(start + 1, end) : []
  const detailIds = new Set(details.map((row) => row.node_id))
  // A provider heading without an explicit outer total is rolled up from its
  // roots. Nested subtotals are already represented by their account parents.
  const valueRows = total ? [total]
    : heading?.values.some((value) => value != null && Math.abs(Number(value)) >= 0.005) ? [heading]
      : details.filter((row) => !row.is_formula && !/^total\s/.test(labelOf(row))
        && (!row.parent_node_id || !detailIds.has(row.parent_node_id)))
  return { heading, total, details, valueRows }
}

/** Use the producer's summary identity before considering display-label totals. */
export function resolveStatementSectionTotal(rows: QuickBooksFpaStatementRow[], row: QuickBooksFpaStatementRow) {
  const cashSection = /^cash_flow:section:(operating|investing|financing)$/.exec(row.node_id)
  const profitAndLoss = Object.values(sections).find((section) => section.headingId === row.node_id)
  const canonicalId = profitAndLoss ? profitAndLoss.totalId : cashSection ? `cash_flow:${cashSection[1]}`
    : row.row_type === 'heading' && /^cash_flow:(operating|investing|financing):type:/.test(row.node_id)
      ? `${row.node_id}:total` : null
  if (canonicalId) {
    const total = rows.find((candidate) => candidate.node_id === canonicalId) || null
    if (total) return { total, authoritative: true }
  }
  const label = labelOf(row).replace(/^total\s+/, '')
  const start = rows.findIndex((candidate) => candidate.node_id === row.node_id)
  const total = (start >= 0 ? rows.slice(start + 1) : rows).find((candidate) =>
    candidate.node_id !== row.node_id
      && (candidate.is_total || candidate.row_type === 'group' || candidate.row_type === 'formula')
      && labelOf(candidate).replace(/^total\s+/, 'total ') === `total ${label}`,
  ) || null
  return { total, authoritative: false }
}

/** A null canonical total remains unavailable; it cannot be replaced by child sums. */
export function statementSectionTotalValue(row: QuickBooksFpaStatementRow, indices: number[], endingBalance = false): number | null {
  const usable = indices.filter((index) => index >= 0 && index < row.values.length)
  const selected = endingBalance ? usable.slice(-1) : usable
  if (!selected.length || selected.some((index) => row.values[index] == null || !Number.isFinite(Number(row.values[index])))) return null
  return selected.reduce((sum, index) => sum + Number(row.values[index]), 0)
}

/** Explicit producer parents govern hierarchy; level-only provider rows retain their nesting. */
export function statementRowParents(rows: QuickBooksFpaStatementRow[]) {
  const parents = new Map<string, string | null>()
  const stack: QuickBooksFpaStatementRow[] = []
  for (const row of rows) {
    while (stack.length && levelOf(stack[stack.length - 1]) >= levelOf(row)) stack.pop()
    parents.set(row.node_id, row.parent_node_id || stack[stack.length - 1]?.node_id || null)
    stack.push(row)
  }
  return parents
}

export function statementRowIsCollapsed(row: QuickBooksFpaStatementRow, parents: Map<string, string | null>, collapsed: Set<string>) {
  let parent = parents.get(row.node_id)
  const visited = new Set<string>([row.node_id])
  while (parent && !visited.has(parent)) {
    if (collapsed.has(parent)) return true
    visited.add(parent)
    parent = parents.get(parent)
  }
  return false
}


const controlLabel = (value: unknown) => String(value || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')
const formulaControls = [
  { id: 'fpa:pnl:gross_profit', labels: ['gross profit'] },
  { id: 'fpa:pnl:operating_income', labels: ['operating income', 'net operating income'] },
  { id: 'fpa:pnl:net_other_income', labels: ['net other income', 'other income expenses', 'other income and expenses'] },
  { id: 'fpa:pnl:net_income', labels: ['net income', 'net earnings'] },
]

export function isCanonicalProfitAndLossControl(row: QuickBooksFpaStatementRow) {
  return Object.values(sections).some((section) => row.node_id === section.totalId)
    || formulaControls.some((control) => row.node_id === control.id)
}

/** Both driver calculation and accounting validation use the producer's control.
 * Display-label duplicates are resolved by their outer statement scope, never
 * by which account subtotal happens to carry more money.
 */
export function findStatementControlRow(rows: QuickBooksFpaStatementRow[], labelSets: string[][]): QuickBooksFpaStatementRow | null {
  const labels = labelSets.map((parts) => controlLabel(parts.join(' ')).replace(/^total\s+/, ''))
  const section = Object.values(sections).find((definition) => definition.labels.some((label) => labels.includes(label)))
  const formula = formulaControls.find((definition) => definition.labels.some((label) => labels.includes(label)))
  const canonicalId = section?.totalId || formula?.id
  const canonical = canonicalId ? rows.find((row) => row.node_id === canonicalId) : null
  if (canonical) return canonical
  const best = (matches: QuickBooksFpaStatementRow[]) => matches.sort((a, b) => {
    const outer = levelOf(a) - levelOf(b)
    if (outer) return outer
    const semantic = (row: QuickBooksFpaStatementRow) => (row.is_total || row.is_formula || /^total\s+/.test(controlLabel(row.label)) ? 2 : 0)
      + (row.row_type === 'group' || row.row_type === 'formula' ? 1 : 0)
    const priority = semantic(b) - semantic(a)
    if (priority) return priority
    const magnitude = (row: QuickBooksFpaStatementRow) => row.values.reduce<number>((sum, value) => sum + Math.abs(Number(value) || 0), 0)
    return magnitude(b) - magnitude(a)
  })[0] || null
  for (const parts of labelSets) {
    const label = controlLabel(parts.join(' '))
    const exact = best(rows.filter((row) => controlLabel(row.label) === label || controlLabel(row.label) === `total ${label}`))
    if (exact) return exact
  }
  for (const parts of labelSets) {
    const fuzzy = best(rows.filter((row) => parts.every((part) => controlLabel(row.label).includes(controlLabel(part)))))
    if (fuzzy) return fuzzy
  }
  return null
}

/** Section subtotals inherit their statement category rather than the broad
 * Expense/Revenue classification of a saved layout group.
 */
export function profitAndLossSectionKey(row: QuickBooksFpaStatementRow): SectionKey | null {
  const identity = Object.entries(sections).find(([, section]) => row.node_id === section.headingId || row.node_id === section.totalId)
  if (identity) return identity[0] as SectionKey
  if (!(row.is_total || row.row_type === 'group' || row.row_type === 'heading')) return null
  const label = controlLabel(row.label).replace(/^total\s+/, '')
  return (Object.entries(sections).find(([, section]) => section.labels.includes(label))?.[0] as SectionKey | undefined) || null
}
